import MultiStep from '@components/@quiz/MultiStep'
import SetupWrapperLayout from '@components/@quiz/MultiStep/SetupWrapperLayout'
import useQuiz from '@utils/hooks/useQuiz'
import { useEffect, useMemo } from 'react'
import _ from 'lodash'
import { Button, Step, StepContent, StepLabel, Stepper } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { SectionsIds } from '@services/common.services'
import { useConfirm } from '@components/Confirm'
import useInit from '@utils/hooks/useInit'
import { Navigate } from 'react-router-dom'
import DataLoading from '@components/DataLoading'
import useToast from '@utils/hooks/useToast'

const InitSetup: React.FC = () => {
  const {
    fetchQuiz,
    quiz,
    setTags,
    setPositions,
    survey,
    quizList,
    meta,
    tagsMeta,
    surveyMeta,
    positionsMeta,
    settingsMeta,
  } = useQuiz()
  const { initApp, initData } = useInit()
  const isSetup = _.get(initData, 'attributes.init_setup', false)
  const confirm = useConfirm()

  const { addToast } = useToast()
  const isSubmitting =
    tagsMeta.pending ||
    surveyMeta.pending ||
    positionsMeta.pending ||
    settingsMeta.pending

  const { t } = useTranslation()

  useEffect(() => {
    if (!quizList && !meta.pending) {
      fetchQuiz()
    }
  }, [])

  const sectionHandlers: Record<SectionsIds, (data: any) => any> = {
    [SectionsIds.SURVEY_SETUP]: (data) => survey({ fields: data }),
    [SectionsIds.COMPANY_SETUP]: (data) => quiz({ fields: data }),
    [SectionsIds.INIT_POSITIONS]: (data) => setPositions(data),
    [SectionsIds.INIT_TAGS]: (data) => setTags(data),
    // Add more handlers as needed
  }

  const allList = useMemo(
    () => _.orderBy(quizList?.sections, ['order_number'], 'asc'),
    [quizList]
  )
  const filterByNotComplete = useMemo(
    () => _.filter(allList, (section) => !section?.is_complete),
    [allList]
  )
  const quizData = useMemo(
    () =>
      !_.isEmpty(filterByNotComplete) ? filterByNotComplete[0] : undefined,
    [filterByNotComplete]
  )
  const activeStep = useMemo(
    () => _.findIndex(allList, (section) => !section?.is_complete),
    [allList]
  )

  const handleData = async (data: any, sectionId: SectionsIds) => {
    const handler = await sectionHandlers[sectionId]

    if (!handler) {
      return null
    }

    handler(data)
      .unwrap()
      .then(() => {
        confirm({
          title: t('QUIZ.confirm_title'),
          description: t('QUIZ.confirm_descriptions'),
          sweet_alert: 'success',
        })
          .then(() => {
            // timeout
            fetchQuiz()
            initApp()
          })
          .catch(() => fetchQuiz())
      })
      .catch(() => {
        addToast({
          message: t('QUIZ.submit_failed'),
          severity: 'warning',
          anchorOrigin: {
            horizontal: 'right',
            vertical: 'top',
          },
        })
      })
  }

  if (!isSetup) {
    return <Navigate to="/" />
  }

  return (
    <SetupWrapperLayout
      leftSide={
        <>
          <Stepper activeStep={activeStep} orientation="vertical">
            {allList.map((step) => (
              <Step key={step.id}>
                <StepLabel>{t(`QUIZ.${step.name}`)}</StepLabel>
                <StepContent>{t(`QUIZ.${step.description}`)}</StepContent>
              </Step>
            ))}
          </Stepper>
        </>
      }
    >
      {quizData && (
        <MultiStep
          key={quizData.section_id}
          data={quizData as any}
          onSubmitSection={handleData}
          isSubmitting={isSubmitting}
        />
      )}
      <DataLoading
        isLoading={meta.pending}
        isEmptyData={_.isEmpty(quizData)}
        emptyAction={
          <Button variant="outlined" color="primary">
            {t('SYSCOMMON.retry')}
          </Button>
        }
      />
    </SetupWrapperLayout>
  )
}

export default InitSetup
